import {
  EditContextProvider,
  Form,
  useEditController,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { Button as MUIButton, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Resources } from '../../resource';
import LeaderboardCycleForm from './LeaderboardCycleForm';
import {
  updateLeaderboardCycle,
  LeaderboardCycle,
  CycleStatus,
  UpdateLeaderboardCycleInput,
} from '../../../services/redpacketService';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { PermissionButton } from '../../../../../routes/components/permission-button';

const EditLeaderboardCycle = ({
  id,
  record,
  onClose,
}: {
  id: string | number;
  record: LeaderboardCycle;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const t = useTranslate();
  const refresh = useRefresh();

  const controller = useEditController({
    id,
    resource: Resources.redpacket,
    mutationMode: 'pessimistic',
    mutationOptions: {
      onSuccess: () => {
        notify('Update successful', { type: 'success' });
        refresh();
        onClose();
      },
      onError: (error: any) => {
        notify(error.message || 'Update failed', { type: 'error' });
      },
    },
  });

  const CustomSaveButton = () => {
    const { handleSubmit } = useFormContext();
    const onSubmit = async (data: any) => {
      try {
        if (
          data.rewardAutoTransferEnabled &&
          data.rewardTransferTime &&
          data.endTime &&
          !data.clearRewardTransferTime &&
          !dayjs(data.rewardTransferTime).isAfter(dayjs(data.endTime))
        ) {
          notify(t('module.redpacket.rewardTransferMustAfterEnd'), { type: 'warning' });
          return;
        }
        let startTime = data.startTime;
        if (data.status === CycleStatus.ACTIVE) {
          startTime = undefined;
        }
        const payload: UpdateLeaderboardCycleInput = {
          id: String(id),
          startTime: startTime,
          endTime: data.endTime,
          rewardAutoTransferEnabled: !!data.rewardAutoTransferEnabled,
        };
        if (data.clearRewardTransferTime) {
          payload.clearRewardTransferTime = true;
        } else if (data.rewardAutoTransferEnabled && data.rewardTransferTime) {
          payload.rewardTransferTime = data.rewardTransferTime;
        }
        await updateLeaderboardCycle(payload);
        notify('Update successful', { type: 'success' });
        refresh();
        onClose();
      } catch (error: any) {
        notify(error.message || 'Update failed', { type: 'error' });
      }
    };
    return <MUIButton onClick={handleSubmit(onSubmit)}>Save</MUIButton>;
  };

  if (controller.isLoading || (!controller.record && !record)) return null;

  const currentRecord = record || controller.record;

  return (
    <EditContextProvider value={controller}>
      <Form
        disableInvalidFormNotification
        defaultValues={{
          ...currentRecord,
          rewardAutoTransferEnabled: currentRecord.rewardAutoTransferEnabled ?? false,
          rewardTransferTime: currentRecord.rewardTransferTime ?? '',
          clearRewardTransferTime: false,
        }}
      >
        <DialogTitle>Edit Leaderboard Cycle</DialogTitle>
        <DialogContent>
          <LeaderboardCycleForm isCreate={false} record={currentRecord} />
        </DialogContent>
        <DialogActions>
          <MUIButton variant="outlined" onClick={onClose}>
            {t('actions.cancel')}
          </MUIButton>
          <PermissionButton resource="redpacket" action="update">
            <CustomSaveButton />
          </PermissionButton>
        </DialogActions>
      </Form>
    </EditContextProvider>
  );
};

export default EditLeaderboardCycle;
